import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ImageUploadService,
  UploadedImage,
} from '../services/image-upload.service';

@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadComponent implements OnDestroy, OnChanges {
  @Input() maxImages: number = 10;
  @Input() maxSizeMB: number = 20;
  @Input() required: boolean = false;
  @Input() autoUpload: boolean = false;
  @Input() enforceMaxImages: boolean = false;

  @Output() imagesChange = new EventEmitter<string[]>();
  @Output() uploadComplete = new EventEmitter<{
    urls: string[];
    errors: string[];
  }>();
  @Output() uploadProgress = new EventEmitter<{
    uploaded: number;
    total: number;
  }>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  images: UploadedImage[] = [];
  uploadErrors: string[] = [];
  isDragOver = false;
  isUploading = false;
  draggedIndex: number | null = null;
  dragOverIndex: number | null = null;
  isDraggingImage = false;
  Math = Math;
  rotatingImageIndex: number | null = null;

  private readonly ABSOLUTE_MAX_IMAGES = 42;
  private _uploadedCount: number = 0;
  private _totalSize: string = '0 B';
  private _allUploaded: boolean = false;
  private pendingUpdate = false;
  private dragLeaveTimeout?: number;
  private imageCleanupMap = new Map<string, HTMLImageElement>();

  constructor(
    private imageUploadService: ImageUploadService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxImages'] && !changes['maxImages'].firstChange) {
      const newMaxImages = changes['maxImages'].currentValue;
      const previousMaxImages = changes['maxImages'].previousValue;

      if (
        this.enforceMaxImages &&
        newMaxImages < previousMaxImages &&
        this.images.length > newMaxImages
      ) {
        const removedImages = this.images.slice(newMaxImages);
        this.images = this.images.slice(0, newMaxImages);

        removedImages.forEach((image) => {
          this.revokeImagePreview(image);
        });

        this.updateComputedValues();
        this.emitImagesChange();
        this.uploadErrors.push(
          `Plan changed: Removed ${removedImages.length} image(s) to meet the new limit of ${newMaxImages} images.`
        );
        this.scheduleUpdate();
      }
    }

    if (changes['enforceMaxImages']) {
      this.enforceMaxImages = changes['enforceMaxImages'].currentValue;
    }
  }

  ngOnDestroy(): void {
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
    }
    this.images.forEach((image) => this.revokeImagePreview(image));
    this.imageCleanupMap.forEach((img) => this.cleanupImageElement(img));
    this.imageCleanupMap.clear();
    this.imageUploadService.cancelAllUploads();
  }

  private scheduleUpdate(): void {
    if (!this.pendingUpdate) {
      this.pendingUpdate = true;
      Promise.resolve().then(() => {
        this.pendingUpdate = false;
        this.cdr.markForCheck();
      });
    }
  }

  private cleanupImageElement(img: HTMLImageElement): void {
    img.onload = null;
    img.onerror = null;
    img.src = '';
  }

  trackByImage(index: number, image: UploadedImage): string {
    return image.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  triggerFileSelect(): void {
    if (this.isUploading) {
      return;
    }

    if (this.enforceMaxImages && this.images.length >= this.maxImages) {
      return;
    }

    this.fileInput.nativeElement.click();
  }

  handleKeyboardTrigger(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.triggerFileSelect();
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
    }
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isUploading) {
      const shouldShowDrag = this.enforceMaxImages
        ? this.images.length < this.maxImages
        : true;

      if (shouldShowDrag && !this.isDragOver) {
        this.isDragOver = true;
        this.scheduleUpdate();
      }
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
      }

      this.dragLeaveTimeout = window.setTimeout(() => {
        if (this.isDragOver) {
          this.isDragOver = false;
          this.scheduleUpdate();
        }
      }, 50);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
    }

    if (this.isDragOver) {
      this.isDragOver = false;
      this.scheduleUpdate();
    }

    if (this.isUploading) {
      return;
    }

    if (this.enforceMaxImages && this.images.length >= this.maxImages) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onImageDragStart(event: DragEvent, index: number): void {
    if (this.isUploading) {
      event.preventDefault();
      return;
    }

    this.draggedIndex = index;
    this.isDraggingImage = true;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', '');

      const dragElement = event.target as HTMLElement;
      const rect = dragElement.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        dragElement,
        rect.width / 2,
        rect.height / 2
      );
    }

    this.scheduleUpdate();
  }

  onImageDragOver(event: DragEvent, index: number): void {
    event.preventDefault();

    if (this.draggedIndex === null || this.draggedIndex === index) {
      return;
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    if (this.dragOverIndex !== index) {
      this.dragOverIndex = index;
      this.scheduleUpdate();
    }
  }

  onImageDragEnter(event: DragEvent, index: number): void {
    event.preventDefault();

    if (this.draggedIndex !== null && this.draggedIndex !== index) {
      if (this.dragOverIndex !== index) {
        this.dragOverIndex = index;
        this.scheduleUpdate();
      }
    }
  }

  onImageDragLeave(event: DragEvent): void {
    event.preventDefault();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (this.dragOverIndex !== null) {
        this.dragOverIndex = null;
        this.scheduleUpdate();
      }
    }
  }

  onImageDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
      this.resetDragState();
      return;
    }

    this.reorderImages(this.draggedIndex, dropIndex);
    this.resetDragState();
  }

  onImageDragEnd(event: DragEvent): void {
    this.resetDragState();
  }

  private resetDragState(): void {
    const needsUpdate =
      this.draggedIndex !== null ||
      this.dragOverIndex !== null ||
      this.isDraggingImage;

    this.draggedIndex = null;
    this.dragOverIndex = null;
    this.isDraggingImage = false;

    if (needsUpdate) {
      this.scheduleUpdate();
    }
  }

  private reorderImages(fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex) return;

    const imagesCopy = [...this.images];
    const [movedImage] = imagesCopy.splice(fromIndex, 1);
    imagesCopy.splice(toIndex, 0, movedImage);

    this.images = imagesCopy;
    this.emitImagesChange();
    this.scheduleUpdate();
  }

  private async handleFiles(files: File[]): Promise<void> {
    this.uploadErrors = [];

    let availableSlots: number;

    if (this.enforceMaxImages) {
      availableSlots = this.maxImages - this.images.length;
    } else {
      availableSlots = this.ABSOLUTE_MAX_IMAGES - this.images.length;
    }

    if (files.length > availableSlots) {
      const excessCount = files.length - availableSlots;
      this.uploadErrors.push(
        `Cannot add ${excessCount} image(s). Only ${availableSlots} slot(s) available.`
      );
      files = files.slice(0, availableSlots);
    }

    if (files.length === 0) {
      this.scheduleUpdate();
      return;
    }

    const validation = this.imageUploadService.validateImages(files);

    if (!validation.valid) {
      this.uploadErrors = validation.errors;
      this.scheduleUpdate();
      return;
    }

    const newImages: UploadedImage[] = await Promise.all(
      files.map((file) => this.createImagePreview(file))
    );

    this.images = [...this.images, ...newImages];
    this.updateComputedValues();
    this.scheduleUpdate();

    if (this.autoUpload) {
      await this.uploadNewImages(newImages);
    }
  }

  private async createImagePreview(file: File): Promise<UploadedImage> {
    const isHEIC = this.imageUploadService.isHEICFormat(file.type);
    const id = `${file.name}-${file.size}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return new Promise<UploadedImage>(async (resolve) => {
      if (isHEIC) {
        resolve({
          id,
          file,
          url: '',
          preview: '',
          uploaded: false,
          uploading: false,
          error: undefined,
          isHEIC: true,
        });
        return;
      }

      try {
        const orientedPreview = await this.createOrientedPreview(file);
        resolve({
          id,
          file,
          url: '',
          preview: orientedPreview,
          uploaded: false,
          uploading: false,
          error: undefined,
          isHEIC: false,
        });
      } catch (error) {
        const objectUrl = URL.createObjectURL(file);
        resolve({
          id,
          file,
          url: '',
          preview: objectUrl,
          uploaded: false,
          uploading: false,
          error: undefined,
          isHEIC: false,
        });
      }
    });
  }

  private async createOrientedPreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Canvas not supported'));
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          URL.revokeObjectURL(objectUrl);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const orientedUrl = URL.createObjectURL(blob);
                resolve(orientedUrl);
              } else {
                reject(new Error('Failed to create blob'));
              }
            },
            file.type,
            0.95
          );
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = objectUrl;
    });
  }

  private async uploadNewImages(images: UploadedImage[]): Promise<void> {
    const uploadPromises = images.map((image) => this.uploadSingleImage(image));
    await Promise.allSettled(uploadPromises);
  }

  async uploadAllImages(): Promise<void> {
    if (this.isUploading) return;

    this.isUploading = true;

    const imagesToUpload = this.images.filter((img) => !img.uploaded);

    imagesToUpload.forEach((img) => {
      img.uploading = true;
      img.error = undefined;
    });
    this.scheduleUpdate();

    const uploadPromises = imagesToUpload.map((image) =>
      this.uploadSingleImage(image)
    );

    await Promise.allSettled(uploadPromises);

    this.isUploading = false;
    this.updateComputedValues();
    this.scheduleUpdate();

    const urls = this.getUploadedUrls();
    const errors = this.images
      .filter((img) => img.error)
      .map((img) => this.getUploadErrorMessage(img.file.name, img.error));

    this.uploadComplete.emit({ urls, errors });
  }

  private async uploadSingleImage(image: UploadedImage): Promise<void> {
    if (!image.uploading) {
      image.uploading = true;
      this.scheduleUpdate();
    }

    image.error = undefined;

    try {
      const result = await this.imageUploadService.uploadImage(
        image.file,
        undefined,
        true
      );

      if (result.success && result.url) {
        image.url = result.url;
        image.uploaded = true;
        if (image.preview && image.preview.startsWith('blob:')) {
          URL.revokeObjectURL(image.preview);
        }
        image.preview = result.url;

        if (result.conversion) {
          image.conversion = result.conversion;
        }
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error: any) {
      image.error = error.message || 'Upload failed';
      image.uploaded = false;
      this.uploadErrors.push(
        this.getUploadErrorMessage(image.file.name, error)
      );
    } finally {
      image.uploading = false;
      this.updateComputedValues();
      this.emitUploadProgress();
      this.emitImagesChange();
      this.scheduleUpdate();
    }
  }

  private getUploadErrorMessage(fileName: string, error: any): string {
    const category = this.imageUploadService.getErrorCategory(error);

    switch (category) {
      case 'network':
        return `❌ ${fileName}: Connection lost. Check your internet and try again.`;
      case 'auth':
        return `🔒 ${fileName}: Session expired. Please log in again.`;
      case 'validation':
        return `⚠️ ${fileName}: File doesn't meet requirements. Check size and format.`;
      case 'timeout':
        return `⏱️ ${fileName}: Upload timed out. Retrying automatically...`;
      case 'server':
        return `🔧 ${fileName}: Server error. Our team has been notified.`;
      default:
        return `❌ ${fileName}: Upload failed. Please try again.`;
    }
  }

  removeImage(index: number): void {
    if (this.isUploading) return;

    const image = this.images[index];
    this.revokeImagePreview(image);

    this.images.splice(index, 1);
    this.updateComputedValues();
    this.emitImagesChange();
    this.emitUploadProgress();
    this.scheduleUpdate();
  }

  async rotateImage(index: number, degrees: 90 | -90): Promise<void> {
    if (this.isUploading || this.rotatingImageIndex !== null) return;

    const image = this.images[index];
    this.rotatingImageIndex = index;
    this.scheduleUpdate();

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = image.preview || URL.createObjectURL(image.file);
      });

      if (degrees === 90 || degrees === -90) {
        canvas.width = img.height;
        canvas.height = img.width;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const rotatedBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          },
          image.file.type,
          0.95
        );
      });

      const rotatedFile = new File([rotatedBlob], image.file.name, {
        type: image.file.type,
        lastModified: Date.now(),
      });

      const oldPreview = image.preview;
      if (oldPreview && oldPreview.startsWith('blob:')) {
        URL.revokeObjectURL(oldPreview);
      }

      image.file = rotatedFile;
      image.preview = URL.createObjectURL(rotatedFile);
      image.uploaded = false;
      image.url = '';
    } catch (error) {
      console.error('Rotation failed:', error);
      this.uploadErrors.push(`Failed to rotate ${image.file.name}`);
    } finally {
      this.rotatingImageIndex = null;
      this.scheduleUpdate();
    }
  }

  clearAllImages(): void {
    if (this.isUploading) return;

    this.images.forEach((image) => this.revokeImagePreview(image));

    this.images = [];
    this.uploadErrors = [];
    this.updateComputedValues();
    this.emitImagesChange();
    this.emitUploadProgress();
    this.scheduleUpdate();
  }

  private revokeImagePreview(image: UploadedImage): void {
    if (image.preview && image.preview.startsWith('blob:')) {
      URL.revokeObjectURL(image.preview);
    }
    if (this.imageCleanupMap.has(image.id)) {
      const img = this.imageCleanupMap.get(image.id);
      if (img) {
        this.cleanupImageElement(img);
      }
      this.imageCleanupMap.delete(image.id);
    }
  }

  private updateComputedValues(): void {
    this._uploadedCount = this.images.filter((img) => img.uploaded).length;
    this._allUploaded =
      this.images.length > 0 && this.images.every((img) => img.uploaded);

    const totalBytes = this.images.reduce((sum, img) => sum + img.file.size, 0);
    this._totalSize = this.formatFileSize(totalBytes);
  }

  getUploadedCount(): number {
    return this._uploadedCount;
  }

  getTotalSize(): string {
    return this._totalSize;
  }

  allImagesUploaded(): boolean {
    return this._allUploaded;
  }

  getUploadedUrls(): string[] {
    return this.images
      .filter((img) => img.uploaded && img.url)
      .map((img) => img.url);
  }

  getRemainingSlots(): number {
    const maxAllowed = this.enforceMaxImages
      ? this.maxImages
      : this.ABSOLUTE_MAX_IMAGES;
    return maxAllowed - this.images.length;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  hasUploadErrors(): boolean {
    return this.uploadErrors.length > 0 || this.images.some((img) => img.error);
  }

  private emitImagesChange(): void {
    const urls = this.getUploadedUrls();
    this.imagesChange.emit(urls);
  }

  private emitUploadProgress(): void {
    this.uploadProgress.emit({
      uploaded: this.getUploadedCount(),
      total: this.images.length,
    });
  }

  getCurrentImageUrls(): string[] {
    return this.getUploadedUrls();
  }

  isUploadInProgress(): boolean {
    return this.isUploading || this.images.some((img) => img.uploading);
  }

  isReadyForSubmission(): { ready: boolean; message?: string } {
    if (this.required && this.images.length === 0) {
      return { ready: false, message: 'At least one image is required' };
    }

    if (this.isUploadInProgress()) {
      return {
        ready: false,
        message: 'Please wait for image uploads to complete',
      };
    }

    if (this.hasUploadErrors()) {
      return { ready: false, message: 'Please resolve upload errors' };
    }

    if (this.images.length > 0 && !this.allImagesUploaded()) {
      return { ready: false, message: 'Please upload all selected images' };
    }

    if (this.enforceMaxImages && this.images.length > this.maxImages) {
      return {
        ready: false,
        message: `Too many images. Maximum ${this.maxImages} allowed for your current plan.`,
      };
    }

    return { ready: true };
  }

  needsPlanUpgrade(): boolean {
    return this.enforceMaxImages && this.images.length > this.maxImages;
  }

  getMinimumPlanNeeded(): string {
    if (this.images.length <= 7) return 'Free';
    if (this.images.length <= 14) return 'Starter';
    if (this.images.length <= 21) return 'Pro';
    return 'Elite';
  }

  getCurrentLimitsText(): string {
    const maxAllowed = this.enforceMaxImages
      ? this.maxImages
      : this.ABSOLUTE_MAX_IMAGES;
    return `${
      this.images.length
    }/${maxAllowed} images (${this.getRemainingSlots()} remaining)`;
  }

  isApproachingLimit(): boolean {
    const maxAllowed = this.enforceMaxImages
      ? this.maxImages
      : this.ABSOLUTE_MAX_IMAGES;
    return this.images.length >= maxAllowed * 0.8;
  }

  isAtLimit(): boolean {
    const maxAllowed = this.enforceMaxImages
      ? this.maxImages
      : this.ABSOLUTE_MAX_IMAGES;
    return this.images.length >= maxAllowed;
  }

  getConvertedFormat(image: UploadedImage): string {
    if (!image.conversion?.converted) return '';

    const format = image.conversion.converted_format
      .split('/')[1]
      .toUpperCase();
    return format === 'JPEG' ? 'JPG' : format;
  }

  getAcceptAttribute(): string {
    return this.imageUploadService.getAcceptAttribute();
  }

  getSupportedFormatsText(): string {
    return this.imageUploadService.getSupportedFormatsForDisplay();
  }

  hasLimitedBrowserSupport(image: UploadedImage): boolean {
    return this.imageUploadService.hasLimitedBrowserSupport(image.file.type);
  }

  getBrowserCompatibilityWarning(image: UploadedImage): string | null {
    return this.imageUploadService.getBrowserCompatibilityWarning(
      image.file.type
    );
  }

  getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toUpperCase() || '';
  }

  isModernFormat(mimeType: string): boolean {
    const modernFormats = [
      'image/avif',
      'image/heic',
      'image/heif',
      'image/jxl',
      'image/webp',
    ];
    return modernFormats.includes(mimeType);
  }

  getFormatBadgeClass(mimeType: string): string {
    if (this.isModernFormat(mimeType)) {
      return 'format-badge modern';
    }
    if (this.imageUploadService.hasLimitedBrowserSupport(mimeType)) {
      return 'format-badge limited';
    }
    return 'format-badge standard';
  }

  hasAnyLimitedSupportImages(): boolean {
    return this.images.some((image) => this.hasLimitedBrowserSupport(image));
  }

  wasImageConverted(image: UploadedImage): boolean {
    return image.conversion?.converted || false;
  }

  getConversionInfo(image: UploadedImage): string {
    if (!image.conversion?.converted) return '';

    const originalFormat = image.conversion.original_format
      .split('/')[1]
      .toUpperCase();
    const convertedFormat = image.conversion.converted_format
      .split('/')[1]
      .toUpperCase();
    const ratio = image.conversion.compression_ratio;

    return `Converted from ${originalFormat} to ${convertedFormat} (${ratio}% size reduction)`;
  }

  hasAnyConvertedImages(): boolean {
    return this.images.some((image) => this.wasImageConverted(image));
  }

  getTotalConversionSavings(): string {
    const conversions = this.images.filter((img) => img.conversion?.converted);
    if (conversions.length === 0) return '';

    const totalOriginal = conversions.reduce(
      (sum, img) => sum + (img.conversion?.original_size || 0),
      0
    );
    const totalConverted = conversions.reduce(
      (sum, img) => sum + (img.conversion?.converted_size || 0),
      0
    );
    const savedBytes = totalOriginal - totalConverted;
    const savedPercent = ((savedBytes / totalOriginal) * 100).toFixed(1);

    return `${this.formatFileSize(
      savedBytes
    )} saved (${savedPercent}% reduction)`;
  }

  getConvertedImagesCount(): number {
    return this.images.filter((img) => this.wasImageConverted(img)).length;
  }

  getFormatDisplayName(mimeType: string): string {
    const formatNames: { [key: string]: string } = {
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPEG',
      'image/png': 'PNG',
      'image/webp': 'WebP',
      'image/gif': 'GIF',
      'image/bmp': 'BMP',
      'image/tiff': 'TIFF',
      'image/avif': 'AVIF',
      'image/heic': 'HEIC',
      'image/heif': 'HEIF',
      'image/jxl': 'JPEG XL',
      'image/jp2': 'JPEG 2000',
      'image/x-icon': 'ICO',
    };

    return formatNames[mimeType] || mimeType.split('/')[1].toUpperCase();
  }

  hasFormatCompatibilityIssues(): boolean {
    return this.hasAnyLimitedSupportImages();
  }

  hasOptimizationBenefits(): boolean {
    return this.hasAnyConvertedImages();
  }

  getUploadStatusSummary(): string {
    const total = this.images.length;
    const uploaded = this.getUploadedCount();
    const converted = this.getConvertedImagesCount();

    if (total === 0) return 'No images selected';
    if (uploaded === 0)
      return `${total} image${total !== 1 ? 's' : ''} ready to upload`;
    if (uploaded < total) return `${uploaded}/${total} images uploaded`;

    let summary = `${total} image${
      total !== 1 ? 's' : ''
    } uploaded successfully`;
    if (converted > 0) {
      summary += ` (${converted} converted)`;
    }
    return summary;
  }
}
